import type { DragEndEvent, DragOverEvent } from '@dnd-kit/core';
import type {
  DragEndEvent as ReactDragEndEvent,
  DragMoveEvent as ReactDragMoveEvent,
  DragOverEvent as ReactDragOverEvent,
  DragStartEvent as ReactDragStartEvent,
} from '@dnd-kit/react';

import { EDITOR_COLLECTION_ITEM_DRAG_TYPE } from '@/components/blocks/editor/collection/collection-dnd';
import { readRecord } from '@/lib/prop-utils';

export type DropValidationEventInput =
  | DragEndEvent
  | DragOverEvent
  | ReactDragEndEvent
  | ReactDragOverEvent
  | ReactDragMoveEvent
  | ReactDragStartEvent;

export type NormalizedRect = {
  top: number;
  left: number;
  width: number;
  height: number;
  bottom?: number;
  right?: number;
};
type NormalizedRectOrNull = NormalizedRect | null;

const toNormalizedRect = (rect: NormalizedRect | null | undefined): NormalizedRectOrNull => {
  if (!rect) {
    return null;
  }

  const right = rect.right ?? rect.left + rect.width;
  const bottom = rect.bottom ?? rect.top + rect.height;

  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
    right,
    bottom,
  };
};

const isOperationEvent = (
  event: DropValidationEventInput,
): event is ReactDragStartEvent | ReactDragOverEvent | ReactDragMoveEvent | ReactDragEndEvent => {
  const record = readRecord(event);
  return Boolean(record && 'operation' in record);
};

export type EventOverSnapshot = {
  id: string;
  data: { current: unknown };
  rect: NormalizedRectOrNull;
  disabled: boolean;
};

export const getEventActiveId = (event: DropValidationEventInput): string => {
  if (isOperationEvent(event)) {
    return String(event.operation.source?.id ?? '');
  }
  return String(event.active.id);
};

export const getEventActiveData = <T = unknown>(event: DropValidationEventInput): T | undefined => {
  if (isOperationEvent(event)) {
    return event.operation.source?.data as T | undefined;
  }
  return event.active.data.current as T | undefined;
};

export const unwrapDndKitData = <T extends Record<string, unknown> = Record<string, unknown>>(
  data: unknown,
): T | undefined => {
  if (data == null) {
    return undefined;
  }
  const record = data as Record<string, unknown>;
  const inner = record.current;
  if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
    return inner as T;
  }
  return data as T;
};

export const isEditorCollectionItemDragEvent = (event: DropValidationEventInput): boolean => {
  const data = getEventActiveData<unknown>(event);
  const payload = unwrapDndKitData<Record<string, unknown>>(data);
  return readRecord(payload)?.type === EDITOR_COLLECTION_ITEM_DRAG_TYPE;
};

export const getEventDelta = (event: DropValidationEventInput): { x: number; y: number } => {
  if (isOperationEvent(event)) {
    return {
      x: event.operation.transform.x ?? 0,
      y: event.operation.transform.y ?? 0,
    };
  }
  return event.delta;
};

export const getEventOver = (event: DropValidationEventInput): EventOverSnapshot | null => {
  if (isOperationEvent(event)) {
    const target = event.operation.target;
    if (!target) {
      return null;
    }
    return {
      id: String(target.id),
      data: { current: target.data },
      rect: toNormalizedRect(target.shape as unknown as NormalizedRect),
      disabled: Boolean(target.disabled),
    };
  }

  if (!event.over) {
    return null;
  }

  return {
    id: String(event.over.id),
    data: { current: event.over.data.current },
    rect: toNormalizedRect(event.over.rect),
    disabled: Boolean(event.over.disabled),
  };
};

export const getEventActivator = (event: DropValidationEventInput): Event | null => {
  if (isOperationEvent(event)) {
    return ('nativeEvent' in event ? event.nativeEvent : null) ?? event.operation.activatorEvent;
  }
  return (event.activatorEvent as Event | null) ?? null;
};

export const getEventActiveTranslatedRect = (
  event: DropValidationEventInput,
): NormalizedRect | null => {
  if (isOperationEvent(event)) {
    return toNormalizedRect(event.operation.shape as unknown as NormalizedRect);
  }
  return toNormalizedRect(event.active.rect.current.translated);
};
